import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { useCurrency } from "../../contexts/CurrencyContext";
import { useBusinessPlan } from "../../App";
import { CURRENCIES, getCurrencySymbol } from "../../constants/currencies";

const ProductFormDialog = ({
  open,
  onOpenChange,
  productForm,
  setProductForm,
  onSubmit,
  standardProductCategories = [],
  standardServiceCategories = [],
  revenueStreams = [],
  teamMembers = [],
  isUnforeseen = false,
  editingItem = null
}) => {
  const { displayCurrency } = useCurrency();
  const { currentPlan } = useBusinessPlan();
  
  // Debug logging
  console.log('🔍 ProductForm currency:', productForm.currency);
  console.log('🔍 Display currency:', displayCurrency);
  console.log('🔍 Plan currency:', currentPlan?.currency);
  console.log('🔍 Final currency:', productForm.currency || currentPlan?.currency || 'USD');
  console.log('🔍 CURRENCIES array:', CURRENCIES);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isUnforeseen ? 'Record Unforeseen ' : (editingItem ? 'Edit ' : 'Add New ')}
            {productForm.type === 'services' ? 'Service' : 'Product'}
            {isUnforeseen && ' Sales'}
          </DialogTitle>
          <DialogDescription>
            {isUnforeseen 
              ? 'Use the Strategic Framework form to capture full details for this unforeseen sale'
              : (editingItem 
                ? `Update ${productForm.type === 'services' ? 'service' : 'product'} information and link it to a revenue stream.`
                : `Add a new ${productForm.type === 'services' ? 'service' : 'product'} and link it to a revenue stream.`)
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="product-sku">SKU *</Label>
              <Input
                id="product-sku"
                placeholder="e.g., PRD-001"
                value={productForm.sku}
                onChange={(e) => setProductForm(prev => ({...prev, sku: e.target.value}))}
                className="form-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="product-type">Type *</Label>
              <Select 
                value={productForm.type} 
                onValueChange={(value) => {
                  setProductForm(prev => ({...prev, type: value, category: ""}));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="products">Products</SelectItem>
                  <SelectItem value="services">Services</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-name">
              {productForm.type === 'services' ? 'Service' : 'Product'} Name *
            </Label>
            <Input
              id="product-name"
              placeholder={`e.g., ${productForm.type === 'services' ? 'Consulting Service' : 'Software License'}`}
              value={productForm.name}
              onChange={(e) => setProductForm(prev => ({...prev, name: e.target.value}))}
              className="form-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-description">Description</Label>
            <Textarea
              id="product-description"
              placeholder="Detailed description..."
              value={productForm.description}
              onChange={(e) => setProductForm(prev => ({...prev, description: e.target.value}))}
              className="form-input"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="product-category">Category</Label>
              <Select 
                value={productForm.category} 
                onValueChange={(value) => {
                  setProductForm(prev => ({...prev, category: value}));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${productForm.type === 'services' ? 'service' : 'product'} category`} />
                </SelectTrigger>
                <SelectContent>
                  {(productForm.type === 'services' ? standardServiceCategories : standardProductCategories).map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="revenue-stream">Revenue Stream {!isUnforeseen && '*'}</Label>
              <Select 
                value={productForm.linked_revenue_stream_id} 
                onValueChange={(value) => setProductForm(prev => ({...prev, linked_revenue_stream_id: value}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select revenue stream to link this product to" />
                </SelectTrigger>
                <SelectContent>
                  {revenueStreams.map(stream => (
                    <SelectItem key={stream.id} value={stream.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{stream.stream_name}</span>
                        {stream.initiative_name && (
                          <span className="text-xs text-slate-500">
                            → {stream.initiative_name}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Base Price, Currency, and COGS */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="base-price">Base Price *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                  {getCurrencySymbol(productForm.currency || currentPlan?.currency || 'USD')}
                </span>
                <Input
                  id="base-price"
                  type="number"
                  placeholder="e.g., 99.99"
                  value={productForm.base_price}
                  onChange={(e) => setProductForm(prev => ({...prev, base_price: e.target.value}))}
                  className="pl-8"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="currency">Currency *</Label>
              <Select
                value={productForm.currency || currentPlan?.currency || 'USD'}
                onValueChange={(value) => setProductForm(prev => ({...prev, currency: value}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((curr) => (
                    <SelectItem key={curr.value} value={curr.value}>
                      {getCurrencySymbol(curr.value)} {curr.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cogs-percentage">COGS (%)</Label>
              <Input
                id="cogs-percentage"
                type="number"
                placeholder="e.g., 30"
                value={productForm.cogs_percentage}
                onChange={(e) => setProductForm(prev => ({...prev, cogs_percentage: e.target.value}))}
                className="form-input"
              />
            </div>
          </div>
          
          {/* Currency conversion notice */}
          {productForm.currency && displayCurrency && productForm.currency !== displayCurrency && (
            <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
              ℹ️ This product price is in {productForm.currency}, will display as {displayCurrency} when viewing
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="product-owner">Product Owner</Label>
            <Select 
              value={productForm.ownerCustom ? "__custom" : (productForm.owner || "")} 
              onValueChange={(val) => {
                if (val === "__custom") {
                  setProductForm(prev => ({...prev, owner: "", ownerCustom: true}));
                } else {
                  setProductForm(prev => ({...prev, owner: val, ownerCustom: false}));
                }
              }}
            >
              <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
              <SelectContent>
                {teamMembers.map(member => (
                  <SelectItem key={member.id} value={member.name}>
                    {member.name} {member.role && `(${member.role})`}
                  </SelectItem>
                ))}
                <SelectItem value="__custom">+ Enter Custom Name</SelectItem>
              </SelectContent>
            </Select>
            {productForm.ownerCustom && (
              <Input 
                autoFocus
                placeholder="Enter custom owner name"
                value={productForm.owner}
                onChange={(e) => setProductForm(prev => ({...prev, owner: e.target.value}))}
                className="mt-2"
              />
            )}
          </div>

          <div className="border rounded-lg p-4 bg-blue-50">
            <h4 className="font-medium text-blue-800 mb-3">Sales Growth Tracking</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="growth-rate">Annual Sales Volume Growth (%)</Label>
                <Input
                  id="growth-rate"
                  type="number"
                  placeholder="e.g., 10"
                  value={productForm.annual_sales_volume_growth}
                  onChange={(e) => setProductForm(prev => ({...prev, annual_sales_volume_growth: e.target.value}))}
                  className="form-input"
                  disabled={productForm.use_per_year_rates}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="base-year-units">Base Year Units</Label>
                <Input
                  id="base-year-units"
                  type="number"
                  placeholder="e.g., 1000"
                  value={productForm.base_year_units}
                  onChange={(e) => setProductForm(prev => ({...prev, base_year_units: e.target.value}))}
                  className="form-input"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="use-per-year-rates"
                checked={productForm.use_per_year_rates}
                onCheckedChange={(checked) => setProductForm(prev => ({...prev, use_per_year_rates: checked}))}
              />
              <label htmlFor="use-per-year-rates" className="text-sm">
                Use different growth rates per year
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="launch-year">Launch Year *</Label>
              <Input
                id="launch-year"
                type="number"
                placeholder="e.g., 2025"
                value={productForm.launch_year}
                onChange={(e) => setProductForm(prev => ({...prev, launch_year: e.target.value}))}
                className="form-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end-year">End Year (Optional)</Label>
              <Input
                id="end-year"
                type="number"
                placeholder="Leave empty if ongoing"
                value={productForm.end_year}
                onChange={(e) => setProductForm(prev => ({...prev, end_year: e.target.value}))}
                className="form-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-end space-x-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={!productForm.name || !productForm.base_price || !productForm.sku || !productForm.launch_year}
              className={isUnforeseen ? "bg-orange-600 hover:bg-orange-700" : ""}
            >
              {isUnforeseen ? 'Continue to Sales Entry' : (editingItem ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormDialog;
